import { type ComponentProps, type ElementType } from 'react';

import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const textVariants = cva('', {
  variants: {
    variant: {
      // page title — matches the ContentLayout page header
      title: 'text-2xl font-bold',
      heading: 'text-lg font-semibold',
      subheading: 'text-sm font-medium',
      body: 'text-sm',
      muted: 'text-sm text-muted-foreground',
      caption: 'text-xs text-muted-foreground',
    },
  },
  defaultVariants: {
    variant: 'body',
  },
});

type TextVariant = NonNullable<VariantProps<typeof textVariants>['variant']>;

// sensible semantic element per variant — keeps headings real <h*> tags for a11y.
// Override with the `as` prop when the context needs a different element.
const defaultElement: Record<TextVariant, ElementType> = {
  title: 'h1',
  heading: 'h2',
  subheading: 'h3',
  body: 'p',
  muted: 'p',
  caption: 'span',
};

export const Text = ({
  className,
  variant,
  as,
  ...props
}: ComponentProps<'p'> &
  VariantProps<typeof textVariants> & { as?: ElementType }) => {
  const Comp = as ?? defaultElement[variant ?? 'body'];
  return (
    <Comp
      data-slot="text"
      className={cn(textVariants({ variant }), className)}
      {...props}
    />
  );
};

export { textVariants };
